import { useCallback, useMemo, useState } from 'react';
import { Loader } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import { DialogOptions } from './types';

export interface DialogProps {
    open: boolean;
    onClose: () => void;
    onCancel?: () => Promise<void>;
    onConfirm: () => Promise<void>;
    options: DialogOptions;
}

export const DialogComponent = ({ open, options, onCancel, onConfirm, onClose }: DialogProps) => {
    const [waitingConfirmation, setWaitingConfirmation] = useState(false);
    const [waitingCancellation, setWaitingCancellation] = useState(false);

    const {
        title,
        description,
        content,
        confirmationText,
        confirmationLoadingText,
        cancellationText,
        cancellationLoadingText,
        allowClose,
        dialogProps,
        dialogContentProps,
        dialogFooterProps,
        dialogFooterContent,
        confirmationButtonProps,
        cancellationButtonProps,
    } = options;

    const handleClose = useCallback(() => {
        //Run the custom callback from useDialog options (if provided)
        options.onClose?.();
        // Run the system close (hides the dialog)
        onClose();
    }, [onClose, options]);

    const contentNode = useMemo(() => {
        if (typeof content === 'function') {
            return content(handleClose);
        }
        return content;
    }, [content, handleClose]);

    return (
        <Dialog open={open} onOpenChange={allowClose ? handleClose : undefined} {...dialogProps}>
            <DialogContent {...dialogContentProps} className={cn('flex flex-col', dialogContentProps?.className)}>
                {(title || (description !== null && description !== undefined)) && (
                    <DialogHeader>
                        {title && <DialogTitle>{title}</DialogTitle>}
                        {description !== null && description !== undefined && (
                            <DialogDescription>{description}</DialogDescription>
                        )}
                    </DialogHeader>
                )}
                <div className="overflow-auto grow">{contentNode}</div>
                {(confirmationText || cancellationText) && (
                    <DialogFooter {...dialogFooterProps}>
                        {dialogFooterContent}
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end grow justify-end">
                            {cancellationText && (
                                <Button
                                    variant="outline"
                                    {...cancellationButtonProps}
                                    disabled={
                                        waitingConfirmation || waitingCancellation || cancellationButtonProps?.disabled
                                    }
                                    onClick={async (e) => {
                                        setWaitingCancellation(true);
                                        try {
                                            // Execute the custom onClick handler first if provided
                                            await cancellationButtonProps?.onClick?.(e);
                                            // Close the dialog after cancel action
                                            await onCancel?.();
                                        } catch (error) {
                                            console.error('Dialog cancellation failed:', error);
                                        } finally {
                                            setWaitingCancellation(false);
                                        }
                                    }}
                                >
                                    {waitingCancellation ? (
                                        <>
                                            <Loader className="animate-spin" />
                                            {cancellationLoadingText || cancellationText}
                                        </>
                                    ) : (
                                        cancellationText
                                    )}
                                </Button>
                            )}
                            {confirmationText && (
                                <Button
                                    {...confirmationButtonProps}
                                    disabled={
                                        waitingConfirmation || waitingCancellation || confirmationButtonProps?.disabled
                                    }
                                    onClick={async (e) => {
                                        setWaitingConfirmation(true);
                                        try {
                                            // Execute the custom onClick handler first (e.g., API call)
                                            await confirmationButtonProps?.onClick?.(e);
                                            // Only close the dialog if the onClick handler succeeds
                                            await onConfirm();
                                        } catch (error) {
                                            // Keep dialog open on error, onClick handler should handle error display
                                            console.error('Dialog confirmation failed:', error);
                                        } finally {
                                            setWaitingConfirmation(false);
                                        }
                                    }}
                                >
                                    {waitingConfirmation ? (
                                        <>
                                            <Loader className="animate-spin" />
                                            {confirmationLoadingText || confirmationText}
                                        </>
                                    ) : (
                                        confirmationText
                                    )}
                                </Button>
                            )}
                        </div>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
};
